import type { ProductFeatureGroup } from "@itaas/pep-contracts";
import type {
  ProductAudienceItem,
  ProductDetailHdConfig,
  ProductOutcomeItem,
  ProductTrustChip,
} from "./product-detail-types";

export const AUTOID_HERO_PRODUCT_ASSET =
  "/assets/products/autoid/hero/itaas-autoid-product-dashboard.png";
export const AUTOID_HERO_HUMAN_ASSET =
  "/assets/products/autoid/hero/itaas-autoid-field-operator.png?v=1";

/** Capture terrain — barcodes/QR/labels/scanners (SoT). RFID/NFC not claimed. */
export const AUTOID_AUDIENCES: ProductAudienceItem[] = [
  {
    id: "warehouse",
    icon: "📦",
    titleFr: "Magasin & logistique",
    titleEn: "Warehouse & logistics",
    bodyFr: "Accélérez réceptions et inventaires avec scan code-barres / QR en temps réel.",
    bodyEn: "Speed receipts and inventory counts with real-time barcode / QR scanning.",
  },
  {
    id: "ops",
    icon: "🏭",
    titleFr: "Opérations terrain",
    titleEn: "Field operations",
    bodyFr: "Connectez lecteurs USB/HID et terminaux mobiles au journal des scans.",
    bodyEn: "Connect USB/HID readers and mobile terminals to the scan journal.",
  },
  {
    id: "it",
    icon: "🖥️",
    titleFr: "IT / intégration",
    titleEn: "IT / integration",
    bodyFr: "Alimentez Inventory, ERP et sécurité via SDK et intégrations Suite.",
    bodyEn: "Feed Inventory, ERP and security via SDK and Suite integrations.",
  },
  {
    id: "pharmacy",
    icon: "💊",
    titleFr: "Pharmacie & santé",
    titleEn: "Pharmacy & healthcare",
    bodyFr: "Réduisez les erreurs de saisie lors de la dispensation par scan.",
    bodyEn: "Reduce entry errors during dispense-by-scan workflows.",
  },
  {
    id: "mining",
    icon: "⛏️",
    titleFr: "Sites miniers",
    titleEn: "Mining sites",
    bodyFr: "Traçabilité scan partagée pour magasins ISRM et équipements terrain.",
    bodyEn: "Shared scan traceability for ISRM stores and field equipment.",
  },
];

export const AUTOID_OUTCOMES: ProductOutcomeItem[] = [
  {
    id: "speed",
    icon: "⚡",
    titleFr: "Réceptions plus rapides",
    titleEn: "Faster receiving",
    bodyFr: "Étiquetage et scan immédiats pour fluidifier les flux magasin.",
    bodyEn: "Immediate labeling and scanning to streamline warehouse flows.",
  },
  {
    id: "errors",
    icon: "🎯",
    titleFr: "Moins d’erreurs de saisie",
    titleEn: "Fewer entry errors",
    bodyFr: "Le décodage code-barres / QR remplace la frappe manuelle fragile.",
    bodyEn: "Barcode / QR decoding replaces fragile manual typing.",
  },
  {
    id: "trace",
    icon: "📋",
    titleFr: "Journal de scans",
    titleEn: "Scan journal",
    bodyFr: "Historique horodaté partagé entre stock, mines et pharmacie.",
    bodyEn: "Timestamped history shared across stock, mining and pharmacy.",
  },
  {
    id: "suite",
    icon: "🧩",
    titleFr: "Intégré Inventory & ERP",
    titleEn: "Integrated with Inventory & ERP",
    bodyFr: "AutoID alimente les opérations Suite sans silo de capture.",
    bodyEn: "AutoID feeds Suite operations without a capture silo.",
  },
];

export const AUTOID_CAPABILITY_GROUPS: ProductFeatureGroup[] = [
  {
    titleFr: "Lecteurs & terminaux",
    titleEn: "Readers & terminals",
    features: [
      "Enregistrement lecteurs et terminaux",
      "Connexion lecteurs USB/HID",
      "Diagnostics lecteurs",
    ],
  },
  {
    titleFr: "Codes & étiquettes",
    titleEn: "Codes & labels",
    features: [
      "Décodage et génération codes-barres",
      "Décodage et génération QR codes",
      "Génération scripts étiquettes",
    ],
  },
  {
    titleFr: "Capture temps réel",
    titleEn: "Real-time capture",
    features: [
      "Journal des scans horodaté",
      "Temps réel",
      "SDK scanner mobile",
    ],
  },
  {
    titleFr: "Suite ITaaS",
    titleEn: "ITaaS Suite",
    features: [
      "Intégration Inventory et ERP",
      "Traçabilité pour sécurité et mines",
    ],
  },
];

const AUTOID_TRUST_BASE: ProductTrustChip[] = [
  {
    id: "scan",
    labelFr: "Codes-barres · QR · lecteurs USB/HID",
    labelEn: "Barcodes · QR · USB/HID readers",
  },
  {
    id: "field",
    labelFr: "Capture terrain temps réel",
    labelEn: "Real-time field capture",
  },
  {
    id: "suite",
    labelFr: "Addon Suite — Inventory & ERP",
    labelEn: "Suite add-on — Inventory & ERP",
  },
];

export const AUTOID_HD_CONFIG: ProductDetailHdConfig = {
  productImageSrc: AUTOID_HERO_PRODUCT_ASSET,
  productImageAlt: {
    fr: "Tableau de bord ITaaS AutoID — lecteurs, scans et étiquettes",
    en: "ITaaS AutoID dashboard — readers, scans and labels",
  },
  humanImageSrc: AUTOID_HERO_HUMAN_ASSET,
  humanImageAlt: {
    fr: "Opérateur scannant un colis avec un terminal industriel",
    en: "Operator scanning a parcel with an industrial terminal",
  },
  visualCaption: {
    fr: "Interface produit ITaaS AutoID — identification automatique et traçabilité",
    en: "ITaaS AutoID product interface — automatic identification and traceability",
  },
  heroKicker: {
    fr: "Scan · Codes-barres · QR · Étiquettes · Temps réel",
    en: "Scan · Barcodes · QR · Labels · Real-time",
  },
  audiences: AUTOID_AUDIENCES,
  audienceTitle: {
    fr: "La capture terrain pour chaque opération Suite",
    en: "Field capture for every Suite operation",
  },
  outcomes: AUTOID_OUTCOMES,
  outcomesLead: {
    fr: "Des bénéfices concrets pour accélérer inventaires, réceptions et traçabilité.",
    en: "Practical benefits to speed inventory, receiving and traceability.",
  },
  capabilityGroups: AUTOID_CAPABILITY_GROUPS,
  preferConfigCapabilities: true,
  contextTitle: {
    fr: "Identification automatique pour la Suite",
    en: "Automatic identification for the Suite",
  },
  contextLead: {
    fr: "Produit de capture terrain (codes-barres, QR, lecteurs, étiquettes) — sans affirmation RFID/NFC non établie au catalogue.",
    en: "Field capture product (barcodes, QR, readers, labels) — without undemonstrated RFID/NFC claims from the catalog.",
  },
  contextFallbackPoints: [
    {
      fr: "Lecteurs USB/HID, codes-barres et QR",
      en: "USB/HID readers, barcodes and QR",
    },
    {
      fr: "Journal des scans et génération d’étiquettes",
      en: "Scan journal and label generation",
    },
    {
      fr: "Intégration Inventory, ERP et opérations terrain",
      en: "Integration with Inventory, ERP and field operations",
    },
  ],
  trustBaseChips: AUTOID_TRUST_BASE,
  showAmbassadorLink: false,
  showEditionShowcase: false,
  ctaPriority: "contact",
};
